import { Box, TextField, Popover, Grid, Typography, Button } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import { useState } from "react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

interface DateRangeFilterProps {
  startDate: Date;
  endDate: Date;
  onChange: (startDate: Date, endDate: Date) => void;
}

export default function DateRangeFilter({
  startDate,
  endDate,
  onChange,
}: DateRangeFilterProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [tempStartDate, setTempStartDate] = useState<Dayjs>(dayjs(startDate));
  const [tempEndDate, setTempEndDate] = useState<Dayjs>(dayjs(endDate));

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setTempStartDate(dayjs(startDate));
    setTempEndDate(dayjs(endDate));
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    // Validar período máximo de 1 ano
    const daysDiff = tempEndDate.diff(tempStartDate, "day");
    if (daysDiff > 365) {
      // Ajustar data inicial para manter período de 1 ano
      const adjustedStartDate = tempEndDate.subtract(365, "day");
      onChange(adjustedStartDate.toDate(), tempEndDate.toDate());
    } else {
      onChange(tempStartDate.toDate(), tempEndDate.toDate());
    }
    handleClose();
  };

  const handleStartDateChange = (newDate: Dayjs | null) => {
    if (newDate) {
      setTempStartDate(newDate);
      // Se a data inicial for maior que a final, ajustar a final
      if (newDate.isAfter(tempEndDate)) {
        setTempEndDate(newDate);
      }
    }
  };

  const handleEndDateChange = (newDate: Dayjs | null) => {
    if (newDate) {
      setTempEndDate(newDate);
      // Se a data final for menor que a inicial, ajustar a inicial
      if (newDate.isBefore(tempStartDate)) {
        setTempStartDate(newDate);
      }
    }
  };

  const formatDateRange = () => {
    return `${dayjs(startDate).format("DD/MM/YYYY")} - ${dayjs(endDate).format("DD/MM/YYYY")}`;
  };

  const open = Boolean(anchorEl);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
      <Box>
        <TextField
          fullWidth
          label="Período"
          value={formatDateRange()}
          onClick={handleClick}
          InputProps={{
            readOnly: true,
            endAdornment: <CalendarMonthIcon sx={{ color: "action.active" }} />,
            sx: { 
              cursor: "pointer",
              backgroundColor: "#FFFFFF",
              "&:hover": {
                backgroundColor: "#F8F9FA",
              }
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              cursor: "pointer",
            },
          }}
        />

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            sx: {
              mt: 1,
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              borderRadius: 2,
              overflow: "hidden",
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Selecione o Período
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                  Data Inicial
                </Typography>
                <DateCalendar
                  value={tempStartDate}
                  onChange={handleStartDateChange}
                  maxDate={tempEndDate}
                  sx={{
                    "& .MuiPickersDay-root": {
                      fontSize: "0.875rem",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                  Data Final
                </Typography>
                <DateCalendar
                  value={tempEndDate}
                  onChange={handleEndDateChange}
                  minDate={tempStartDate}
                  maxDate={dayjs()}
                  sx={{
                    "& .MuiPickersDay-root": {
                      fontSize: "0.875rem",
                    },
                  }}
                />
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2, pt: 2, borderTop: "1px solid #E0E0E0" }}>
              <Button onClick={handleClose} variant="outlined" size="small">
                Cancelar
              </Button>
              <Button onClick={handleApply} variant="contained" size="small">
                Aplicar
              </Button>
            </Box>
          </Box>
        </Popover>
      </Box>
    </LocalizationProvider>
  );
}

